/**
 * Geometry for printable paper: graph grids and ruled lines.
 *
 * Everything is in millimetres, which is also the SVG's user unit: the drawing's
 * `viewBox` is the sheet in millimetres and its `width`/`height` are the sheet in real
 * units, so one unit is one printed millimetre. Inch settings convert at exactly 25.4 mm.
 */

import { parseNumberInput } from "@/lib/parse-number";

export const MM_PER_INCH = 25.4;

export type PaperSize = "letter" | "a4";
export type Orientation = "portrait" | "landscape";
export type LengthUnit = "mm" | "in";

/** ANSI Letter is 8.5 × 11 in; ISO A4 is 210 × 297 mm. */
const PAPER_MM: Record<PaperSize, { width: number; height: number }> = {
  letter: { width: 215.9, height: 279.4 },
  a4: { width: 210, height: 297 },
};

export interface Sheet {
  width: number;
  height: number;
  /** The sheet's real size, as CSS/SVG lengths. */
  cssWidth: string;
  cssHeight: string;
}

export function sheetFor(size: PaperSize, orientation: Orientation): Sheet {
  const { width, height } = PAPER_MM[size];
  const landscape = orientation === "landscape";
  const w = landscape ? height : width;
  const h = landscape ? width : height;
  if (size === "letter") {
    return {
      width: w,
      height: h,
      cssWidth: landscape ? "11in" : "8.5in",
      cssHeight: landscape ? "8.5in" : "11in",
    };
  }
  return { width: w, height: h, cssWidth: `${w}mm`, cssHeight: `${h}mm` };
}

export function toMm(value: number, unit: LengthUnit): number {
  return unit === "in" ? value * MM_PER_INCH : value;
}

const FRACTION = /^\s*(?:(\d+)\s+)?(\d+)\s*\/\s*(\d+)\s*$/;

/** A length typed as a decimal ("0.25", "6,35") or a fraction ("1/4", "1 1/4"). */
export function parseLength(raw: string): number | null {
  const fraction = FRACTION.exec(raw);
  if (fraction) {
    const whole = Number(fraction[1] ?? 0);
    const denominator = Number(fraction[3]);
    if (denominator === 0) {
      return null;
    }
    return whole + Number(fraction[2]) / denominator;
  }
  const value = parseNumberInput(raw);
  return value !== null && value > 0 ? value : null;
}

/* ---------------------------------------------------------------- graph paper */

export type GridKind = "square" | "dot" | "isometric";

export interface Line {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  major: boolean;
}

export interface Dot {
  cx: number;
  cy: number;
  major: boolean;
}

export interface GridDrawing {
  lines: Line[];
  dots: Dot[];
  /** Whole squares (or triangle columns/rows) across and down. */
  columns: number;
  rows: number;
}

export interface GridOptions {
  kind: GridKind;
  /** Square side, dot pitch, or triangle side, in mm. */
  spacing: number;
  /** Clear space on every side, in mm. */
  margin: number;
  /** Every Nth line (or dot) is major; 0 for none. */
  majorEvery: number;
}

function isMajor(index: number, every: number): boolean {
  return every > 0 && ((index % every) + every) % every === 0;
}

/** Whole cells only, centred in the space inside the margins. */
function fitCells(length: number, margin: number, pitch: number) {
  const available = Math.max(0, length - 2 * margin);
  const count = Math.floor(available / pitch + 1e-9);
  const span = count * pitch;
  return { count, span, start: (length - span) / 2 };
}

function squareGrid(sheet: Sheet, options: GridOptions): GridDrawing {
  const across = fitCells(sheet.width, options.margin, options.spacing);
  const down = fitCells(sheet.height, options.margin, options.spacing);
  const lines: Line[] = [];
  const dots: Dot[] = [];
  for (let i = 0; i <= across.count; i += 1) {
    const x = across.start + i * options.spacing;
    if (options.kind === "square") {
      lines.push({
        x1: x,
        y1: down.start,
        x2: x,
        y2: down.start + down.span,
        major: isMajor(i, options.majorEvery),
      });
    }
    for (let j = 0; options.kind === "dot" && j <= down.count; j += 1) {
      dots.push({
        cx: x,
        cy: down.start + j * options.spacing,
        major: isMajor(i, options.majorEvery) && isMajor(j, options.majorEvery),
      });
    }
  }
  for (let j = 0; options.kind === "square" && j <= down.count; j += 1) {
    const y = down.start + j * options.spacing;
    lines.push({
      x1: across.start,
      y1: y,
      x2: across.start + across.span,
      y2: y,
      major: isMajor(j, options.majorEvery),
    });
  }
  return { lines, dots, columns: across.count, rows: down.count };
}

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** The part of the line y = y0 + slope·(x − left) that lies inside the box. */
function clipLine(box: Box, y0: number, slope: number): Line | null {
  const yAt = (x: number) => y0 + slope * (x - box.left);
  const xAt = (y: number) => box.left + (y - y0) / slope;
  const lo = Math.max(box.left, Math.min(xAt(box.top), xAt(box.bottom)));
  const hi = Math.min(box.right, Math.max(xAt(box.top), xAt(box.bottom)));
  if (hi - lo < 1e-6) {
    return null;
  }
  return { x1: lo, y1: yAt(lo), x2: hi, y2: yAt(hi), major: false };
}

/**
 * Isometric (triangular) paper: vertical lines plus lines at 30° either side of the
 * horizontal, meeting in equilateral triangles of side `spacing`. Vertical lines are
 * `spacing × √3/2` apart.
 */
function isometricGrid(sheet: Sheet, options: GridOptions): GridDrawing {
  const s = options.spacing;
  const pitch = (s * Math.sqrt(3)) / 2;
  const across = fitCells(sheet.width, options.margin, pitch);
  const down = fitCells(sheet.height, options.margin, s);
  const box: Box = {
    left: across.start,
    top: down.start,
    right: across.start + across.span,
    bottom: down.start + down.span,
  };
  const lines: Line[] = [];
  for (let i = 0; i <= across.count; i += 1) {
    const x = box.left + i * pitch;
    lines.push({
      x1: x,
      y1: box.top,
      x2: x,
      y2: box.bottom,
      major: isMajor(i, options.majorEvery),
    });
  }
  const slope = 1 / Math.sqrt(3);
  const reach = Math.ceil(across.span / (Math.sqrt(3) * s)) + 1;
  for (let k = -reach; k <= down.count + reach; k += 1) {
    for (const direction of [-1, 1]) {
      const line = clipLine(box, box.top + k * s, direction * slope);
      if (line) {
        lines.push({ ...line, major: isMajor(k, options.majorEvery) });
      }
    }
  }
  return { lines, dots: [], columns: across.count, rows: down.count };
}

export function graphPaper(sheet: Sheet, options: GridOptions): GridDrawing {
  return options.kind === "isometric"
    ? isometricGrid(sheet, options)
    : squareGrid(sheet, options);
}

/* --------------------------------------------------------------- lined paper */

export type Ruling = "wide" | "college" | "narrow" | "primary" | "custom";

/** US ruling standards, in inches: wide 11/32, college 9/32, narrow 1/4. */
export const RULING_INCHES: Record<"wide" | "college" | "narrow", number> = {
  wide: 11 / 32,
  college: 9 / 32,
  narrow: 1 / 4,
};

export interface RuledOptions {
  /** Line spacing in mm — or, for primary paper, the writing height (top line to baseline). */
  spacing: number;
  primary: boolean;
  /** Blank space above the first line, in mm. */
  top: number;
  /** Space on the left, right and bottom, in mm. */
  side: number;
  /** Distance of the vertical margin line from the left edge, in mm; null for none. */
  marginLine: number | null;
}

export interface RuledLine {
  y: number;
  kind: "rule" | "top" | "mid" | "base";
}

export interface RuledDrawing {
  lines: RuledLine[];
  x1: number;
  x2: number;
  /** Writing lines (or primary rows) on the page. */
  rows: number;
  marginLine: { x: number; y1: number; y2: number } | null;
}

/**
 * Ruled lines from the top margin down. Primary paper repeats a solid top line, a
 * dashed midline and a solid baseline, with half the writing height left clear
 * between rows for descenders.
 */
export function ruledPaper(sheet: Sheet, options: RuledOptions): RuledDrawing {
  const bottom = sheet.height - options.side;
  const usable = bottom - options.top;
  const lines: RuledLine[] = [];
  let rows: number;
  if (options.primary) {
    const height = options.spacing;
    const pitch = height * 1.5;
    rows = Math.max(0, Math.floor((usable - height) / pitch + 1e-9) + 1);
    for (let row = 0; row < rows; row += 1) {
      const y = options.top + row * pitch;
      lines.push(
        { y, kind: "top" },
        { y: y + height / 2, kind: "mid" },
        { y: y + height, kind: "base" }
      );
    }
  } else {
    rows = Math.max(0, Math.floor(usable / options.spacing + 1e-9) + 1);
    for (let row = 0; row < rows; row += 1) {
      lines.push({ y: options.top + row * options.spacing, kind: "rule" });
    }
  }
  const marginLine =
    options.marginLine === null
      ? null
      : { x: options.marginLine, y1: options.side, y2: bottom };
  return {
    lines,
    x1: options.side,
    x2: sheet.width - options.side,
    rows,
    marginLine,
  };
}

/* --------------------------------------------------------------- true scale */

/**
 * Print CSS that puts the drawing on the page at its real size.
 *
 * `@page` sets the paper and removes the browser's margins (the drawing carries its
 * own), the sheet element is sized in physical units, and the page column's width
 * limit is lifted so an 8.5-inch sheet is not squeezed into a narrower box. The height
 * is a millimetre short of the paper so rounding can never spill a blank second page;
 * the SVG crops that last millimetre (inside the margin) instead of shrinking.
 */
export function truePrintCss(selector: string, sheet: Sheet): string {
  return [
    `@page { size: ${sheet.cssWidth} ${sheet.cssHeight}; margin: 0; }`,
    "@media print {",
    "  [data-tool-page] > div { max-width: none !important; }",
    `  ${selector} { display: block !important; width: ${sheet.cssWidth} !important; height: calc(${sheet.cssHeight} - 1mm) !important; max-width: none !important; margin: 0 !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; }`,
    "}",
  ].join("\n");
}

/* ------------------------------------------------------------------ drawing */

const round = (value: number) => Number(value.toFixed(3));

/** Lines as one SVG path: `M x1 y1 L x2 y2` per line. */
export function linesPath(lines: readonly Line[]): string {
  return lines
    .map(
      (line) =>
        `M${round(line.x1)} ${round(line.y1)}L${round(line.x2)} ${round(line.y2)}`
    )
    .join("");
}

/**
 * Dots as one SVG path of zero-length segments. With `stroke-linecap: round` each one
 * draws as a circle the width of the stroke — thousands of dots, one element.
 */
export function dotsPath(dots: readonly Dot[]): string {
  return dots.map((dot) => `M${round(dot.cx)} ${round(dot.cy)}h0`).join("");
}

/* ------------------------------------------------------------ typed settings */

/** A typed length: "0" is allowed (a zero margin); anything unreadable is null. */
export function readLength(raw: string, unit: LengthUnit): number | null {
  if (raw.trim() === "0") {
    return 0;
  }
  const value = parseLength(raw);
  return value === null ? null : toMm(value, unit);
}

const MIN_GRID_SPACING_MM: Record<GridKind, number> = {
  square: 1,
  dot: 2.5,
  isometric: 3,
};
export const MAX_GRID_SPACING_MM = 50;

export function minGridSpacing(kind: GridKind): number {
  return MIN_GRID_SPACING_MM[kind];
}

export interface SheetResult<D> {
  drawing: D | null;
  /** The spacing read from the input, in mm (0 if unreadable). */
  spacing: number;
  error: "spacing" | "margins" | null;
}

/** Graph paper from typed settings, or which setting stops it being drawn. */
export function buildGraphSheet(
  sheet: Sheet,
  kind: GridKind,
  unit: LengthUnit,
  inputs: { spacing: string; margin: string; majorEvery: number }
): SheetResult<GridDrawing> {
  const spacing = readLength(inputs.spacing, unit) ?? 0;
  if (spacing < MIN_GRID_SPACING_MM[kind] || spacing > MAX_GRID_SPACING_MM) {
    return { drawing: null, spacing, error: "spacing" };
  }
  const margin = readLength(inputs.margin, unit);
  if (margin === null) {
    return { drawing: null, spacing, error: "margins" };
  }
  const drawing = graphPaper(sheet, {
    kind,
    spacing,
    margin,
    majorEvery: inputs.majorEvery,
  });
  return drawing.columns < 1 || drawing.rows < 1
    ? { drawing: null, spacing, error: "margins" }
    : { drawing, spacing, error: null };
}

export const RULED_SPACING_MM = { min: 3, max: 40 };
/** Room kept above the first line for a name-and-date line. */
export const HEADER_SPACE_MM = 16;

export interface RuledInputs {
  ruling: Ruling;
  unit: LengthUnit;
  /** Custom line spacing, as typed. */
  custom: string;
  /** Primary writing height, as typed. */
  primary: string;
  top: string;
  side: string;
  marginLine: boolean;
  marginAt: string;
  nameLine: boolean;
}

/** Lined paper from typed settings, or which setting stops it being drawn. */
export function buildRuledSheet(
  sheet: Sheet,
  inputs: RuledInputs
): SheetResult<RuledDrawing> & { top: number } {
  const { ruling } = inputs;
  const primary = ruling === "primary";
  let spacing: number;
  if (ruling === "primary") {
    spacing = readLength(inputs.primary, inputs.unit) ?? 0;
  } else if (ruling === "custom") {
    spacing = readLength(inputs.custom, inputs.unit) ?? 0;
  } else {
    spacing = toMm(RULING_INCHES[ruling], "in");
  }
  const max = primary ? RULED_SPACING_MM.max * 2 : RULED_SPACING_MM.max;
  if (spacing < RULED_SPACING_MM.min || spacing > max) {
    return { drawing: null, spacing, top: 0, error: "spacing" };
  }
  const top = readLength(inputs.top, inputs.unit);
  const side = readLength(inputs.side, inputs.unit);
  if (top === null || side === null) {
    return { drawing: null, spacing, top: 0, error: "margins" };
  }
  const effectiveTop = inputs.nameLine ? Math.max(top, HEADER_SPACE_MM) : top;
  const drawing = ruledPaper(sheet, {
    spacing,
    primary,
    top: effectiveTop,
    side,
    marginLine: inputs.marginLine
      ? readLength(inputs.marginAt, inputs.unit)
      : null,
  });
  return drawing.rows < 1 || drawing.x2 <= drawing.x1
    ? { drawing: null, spacing, top: effectiveTop, error: "margins" }
    : { drawing, spacing, top: effectiveTop, error: null };
}
